import { Card, IconButton, LuIcon, Menu, stopPropagation, Text } from "@overmap-ai/blocks"
import { type FormSubmission, type Stored } from "@overmap-ai/common"
import { selectFormById } from "@overmap-ai/core"
import { gray } from "@radix-ui/colors"
import { cx } from "class-variance-authority"
import { memo, useCallback } from "react"

import { interactiveCardCva } from "../../cva"
import { modelIconMapping } from "../../mappings/icons"
import { useAppSelector } from "../../store"
import { LuIconWithWrapper } from "../Icons/LuIconWithWrapper"
import { FormSubmissionIndexLabel } from "../IndexLabels/FormSubmissionIndexLabel"

interface FormSubmissionListItemProps {
	formSubmission: Stored<FormSubmission>
	onClick: (formSubmission: Stored<FormSubmission>) => void
	onView: (formSubmission: Stored<FormSubmission>) => void
	onUnlink: (formSubmission: Stored<FormSubmission>) => void
	onDelete: (formSubmission: Stored<FormSubmission>) => void
}

export const FormSubmissionListItem = memo<FormSubmissionListItemProps>((props) => {
	const { formSubmission, onClick, onView, onUnlink, onDelete } = props

	const form = useAppSelector(selectFormById(formSubmission.form))!

	const handleClick = useCallback(() => {
		onClick(formSubmission)
	}, [formSubmission, onClick])

	const handleView = useCallback(() => {
		onView(formSubmission)
	}, [formSubmission, onView])

	const handleUnlink = useCallback(() => {
		onUnlink(formSubmission)
	}, [formSubmission, onUnlink])

	const handleDelete = useCallback(() => {
		onDelete(formSubmission)
	}, [formSubmission, onDelete])

	return (
		<Card
			role="listitem"
			className={cx("flex cursor-pointer items-center justify-between gap-2", interactiveCardCva())}
			size="sm"
			onClick={handleClick}
		>
			<div className="flex min-w-0 items-center gap-2">
				<LuIconWithWrapper icon={modelIconMapping.form} color={gray.gray9} inline background />
				<FormSubmissionIndexLabel formSubmissionId={formSubmission.uuid} size="sm" />
				<Text size="sm" accentColor="base" className="truncate">
					{form.name}
				</Text>
			</div>

			<div className="flex items-center gap-2">
				<Menu.Root>
					<Menu.ClickTrigger>
						<IconButton onClick={stopPropagation} variant="ghost" size="sm" accentColor="base">
							<LuIcon icon={modelIconMapping.more} />
						</IconButton>
					</Menu.ClickTrigger>
					<Menu.Content size="sm">
						<Menu.Item onSelect={handleView}>
							<LuIcon icon={modelIconMapping.externalLink} />
							View
						</Menu.Item>
						<Menu.Item onSelect={handleUnlink}>
							<LuIcon icon={modelIconMapping.unlink} />
							Unlink
						</Menu.Item>
						<Menu.Separator />
						<Menu.Item onSelect={handleDelete}>
							<LuIcon icon={modelIconMapping.delete} />
							Delete
						</Menu.Item>
					</Menu.Content>
				</Menu.Root>
			</div>
		</Card>
	)
})
FormSubmissionListItem.displayName = "FormSubmissionListItem"
